import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { CircleNotch, ArrowSquareOut } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { useCurrency } from '../../contexts/CompanyContext';
import { ChartCard, DonutChart } from './FinanceCharts';
import { DataNote } from './DataNote';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AGING_COLORS = { Current: '#059669', '1-30': '#d97706', '31-60': '#ea580c', '61-90': '#dc2626', '90+': '#9f1239' };

// kind: 'customer' (receivables) | 'supplier' (payables) | null
export function PartyBalanceDialog({ kind, onClose }) {
  const { formatCurrency } = useCurrency();
  const [data, setData] = useState(null);
  const isCust = kind === 'customer';

  useEffect(() => {
    if (!kind) return;
    setData(null);
    axios.get(`${API_URL}/reports/soa/${isCust ? 'customers' : 'suppliers'}/summary`)
      .then((r) => setData(r.data))
      .catch(() => setData({ rows: [], totals: {} }));
  }, [kind, isCust]);

  const t = data?.totals || {};
  const rows = (data?.rows || []).filter((r) => Math.abs(r.balance) > 0.01 || (r.total_invoiced || r.total_billed) > 0);

  return (
    <Dialog open={!!kind} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto [&>*]:min-w-0" data-testid="party-balance-dialog">
        <DialogHeader>
          <DialogTitle>{isCust ? 'Accounts Receivable — who owes you' : 'Accounts Payable — who you owe'}</DialogTitle>
          <DialogDescription>Same figures as the Statement of Accounts</DialogDescription>
        </DialogHeader>
        <p className="text-xs rounded-md bg-muted/40 border px-3 py-2" data-testid="party-balance-explain">
          <span className="font-semibold">How it's calculated: </span>
          {isCust
            ? 'For every approved invoice: amount billed − receipts received against it. Receipts not linked to an invoice are applied to the oldest open invoice. Draft, rejected and cancelled invoices are excluded.'
            : 'For every approved supplier bill: bill total − payments made against it. Draft and cancelled bills are excluded.'}
        </p>
        <DataNote />

        {!data ? (
          <div className="flex items-center justify-center h-40"><CircleNotch className="animate-spin text-primary" size={26} /></div>
        ) : (
          <div className="space-y-4 min-w-0">
            <div className="grid grid-cols-3 gap-3">
              <Stat label={isCust ? 'Total invoiced' : 'Total billed'} value={formatCurrency(t.total_invoiced ?? t.total_billed ?? 0)} />
              <Stat label={isCust ? 'Total received' : 'Total paid'} value={formatCurrency(t.total_received ?? t.total_paid ?? 0)} />
              <Stat label="Balance outstanding" value={formatCurrency(t.total_balance || 0)} strong testid="party-balance-total" />
            </div>
            <ChartCard title="Aging of outstanding" testid="party-balance-aging-chart">
              <DonutChart data={Object.entries(t.aging || {}).map(([k, v]) => ({ name: k === 'Current' ? 'Not yet due' : `${k} days overdue`, value: v, color: AGING_COLORS[k] }))} height={180} />
            </ChartCard>
            <div className="rounded-lg border overflow-x-auto">
              <table className="w-full text-xs" data-testid="party-balance-table">
                <thead className="bg-muted/50 uppercase tracking-wide text-muted-foreground">
                  <tr>
                    {[isCust ? 'Customer' : 'Supplier', isCust ? 'Invoiced' : 'Billed', isCust ? 'Received' : 'Paid', 'Balance', 'Current', '1-30', '31-60', '61-90', '90+', ''].map((h, i) => (
                      <th key={h || i} className={`px-2.5 py-2 ${i === 0 ? 'text-left' : 'text-right'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && <tr><td colSpan={10} className="text-center py-6 text-muted-foreground">Nothing outstanding</td></tr>}
                  {rows.map((r) => {
                    const id = r.customer_id || r.supplier_id;
                    return (
                      <tr key={id} className="border-t hover:bg-muted/30">
                        <td className="px-2.5 py-2 font-medium">{r.customer_name || r.supplier_name}</td>
                        <td className="px-2.5 py-2 text-right font-mono">{formatCurrency(r.total_invoiced ?? r.total_billed)}</td>
                        <td className="px-2.5 py-2 text-right font-mono">{formatCurrency(r.total_received ?? r.total_paid)}</td>
                        <td className="px-2.5 py-2 text-right font-mono font-semibold">{formatCurrency(r.balance)}</td>
                        {['Current', '1-30', '31-60', '61-90', '90+'].map((k) => (
                          <td key={k} className="px-2.5 py-2 text-right font-mono">{r.aging?.[k] ? formatCurrency(r.aging[k]) : '—'}</td>
                        ))}
                        <td className="px-2.5 py-2 text-right">
                          <Link to={`/dashboard/accounts/statements?tab=${kind}&id=${id}`} onClick={onClose} className="text-primary inline-flex items-center gap-1" data-testid={`party-balance-open-${id}`}>
                            Statement <ArrowSquareOut size={12} />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, strong, testid }) {
  return (
    <div className={`rounded-lg border p-3 ${strong ? 'bg-primary/5 border-primary/30' : 'bg-card'}`}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 font-semibold" data-testid={testid}>{value}</div>
    </div>
  );
}
